import React, { useEffect, useState } from 'react';
import { Typography, Paper, Container } from '@mui/material';
import { useAuthenticator } from '@aws-amplify/ui-react';
import NameQuestions from './nameQuestions';
import { useGameState } from '../utils/GameManager';
import { useAuthGen2 } from '../hooks/useAuthGen2';
import GameInstructions from './GameInstructions';
import InitialsDisplay from './InitialsDisplay';
import GameCompletion from './GameCompletion';
import GuessInput from './GuessInput';
import VictoryPopup from './VictoryPopup';
import InlineHintReveal from './InlineHintReveal';
import { useGuessForm } from '../hooks/useGuessForm';
import { useGameInstructions } from '../hooks/useGameInstructions';
import { NameGuessProps } from '../types/gameTypes';
import { logTriviaAsked, logTriviaRevealed } from './analytics';


const NameGuess: React.FC<NameGuessProps> = (props) => {
    useAuthenticator((context) => [context.user]);
    const [isMobile, setIsMobile] = useState(/Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    const { isAuthenticated, token, loading: authLoading } = useAuthGen2();

    const { game, loading, recordQuestion, recordTrivia, recordGuess, updateGameCompletion } = useGameState(
        isAuthenticated,
        token || undefined
    );

    const targetFirstName = game?.firstname || props.firstName;
    const targetMiddleName = game?.middlename || props.middleName;
    const targetLastName = game?.lastname || props.lastName;
    const gameQuestions = game?.questions || props.questions;
    const gameHints = game?.trivia || props.trivia;

    // Inline hints state
    const [inlineTrivia, setInlineTrivia] = useState<any[]>([]);
    const [showInlineTriviaButton, setShowInlineTriviaButton] = useState(false);
    const [isRevealingHint, setIsRevealingHint] = useState(false);

    const {
        guessState,
        setGuessState,
        guessError,
        isSubmitting,
        handleNameChange,
        handleSubmit: handleGuessSubmit
    } = useGuessForm();

    const { showInstructions, setShowInstructions } = useGameInstructions(loading, game);

    useEffect(() => {
        if (game && !loading) {
            setGuessState(prevState => ({
                ...prevState,
                score: game.score,
                gameCompleted: prevState.gameCompleted || game.gameCompleted || false
            }));
        }
    }, [game, loading, setGuessState]);

    useEffect(() => {
        if (props.gameCompleted) {
            setGuessState(prevState => ({
                ...prevState,
                gameCompleted: true,
                firstName: targetFirstName || '',
                middleName: targetMiddleName || '',
                lastName: targetLastName || '',
            }));
        }
    }, [props.gameCompleted, targetFirstName, targetMiddleName, targetLastName, setGuessState]);

    useEffect(() => {
        setIsMobile(/Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    }, []);

    // Initialize inline trivia state
    useEffect(() => {
        if (gameHints) {
            const askedTriviaIds = game?.triviaAsks || [];
            const triviaWithAskedState = (gameHints || []).map((t: any) => ({
                ...t,
                asked: askedTriviaIds.includes(parseInt(t.id))
            }));
            setInlineTrivia(triviaWithAskedState);
        }
    }, [gameHints, game?.triviaAsks]);

    // Determine if inline trivia button should be shown (all questions asked)
    useEffect(() => {
        if (game) {
            const standardQuestions = gameQuestions?.standard || [];
            const uniqueQuestions = gameQuestions?.unique || [];
            const allQuestions = [...standardQuestions, ...uniqueQuestions];
            const askedIds = game?.asks || [];

            const allAsked = allQuestions.length > 0 &&
                            allQuestions.every(q => askedIds.includes(parseInt(q.id)));

            setShowInlineTriviaButton(allAsked);
        }
    }, [game, gameQuestions]);

    const handleSubmit = async (event: React.MouseEvent | React.FormEvent) => {
        await handleGuessSubmit(
            event,
            targetFirstName,
            targetMiddleName,
            targetLastName,
            recordGuess,
            updateGameCompletion
        );
    };

    // Handler for inline hint reveal
    const handleInlineHintReveal = async () => {
        if (guessState.gameCompleted || isRevealingHint) {
            return;
        }

        const firstUnaskedTrivia = inlineTrivia.find(t => !t.asked);
        if (!firstUnaskedTrivia) {
            return;
        }

        setIsRevealingHint(true);

        try {
            const triviaId = parseInt(firstUnaskedTrivia.id);

            // Log hint analytics
            logTriviaAsked();
            logTriviaRevealed(firstUnaskedTrivia.trivia);

            await recordTrivia(triviaId);

            // Update local state
            setInlineTrivia(prev =>
                prev.map(t =>
                    parseInt(t.id) === triviaId ? { ...t, asked: true } : t
                )
            );
        } catch (error) {
            console.error('Error revealing inline hint:', error);
        } finally {
            setIsRevealingHint(false);
        }
    };




    return (
        <>
            {/* Show loading while authentication is being resolved */}
            {authLoading && (
                <Container maxWidth="lg" sx={{ mt: { xs: 0.25, sm: 0.2, md: 0.75 }, mb: { xs: 0.25, sm: 0.2, md: 0.75 }, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
                    <Paper elevation={3} sx={{ px: { xs: 1, sm: 4 }, py: 4, textAlign: 'center' }}>
                        <Typography>Loading authentication...</Typography>
                    </Paper>
                </Container>
            )}

            {/* Show loading while game data is being loaded */}
            {!authLoading && loading && (
                <Container maxWidth="lg" sx={{ mt: { xs: 0.25, sm: 0.2, md: 0.75 }, mb: { xs: 0.25, sm: 0.2, md: 0.75 }, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
                    <Paper elevation={3} sx={{ px: { xs: 1, sm: 4 }, py: 4, textAlign: 'center' }}>
                        <Typography>Loading game...</Typography>
                    </Paper>
                </Container>
            )}

            {/* Show game content only when both auth and game are loaded */}
            {!authLoading && !loading && (
                <Container maxWidth="lg" sx={{ mt: { xs: 0.25, sm: 0.2, md: 0.75 }, mb: { xs: 0.25, sm: 0.2, md: 0.75 }, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
                    <InitialsDisplay
                        firstName={targetFirstName}
                        middleName={targetMiddleName}
                        lastName={targetLastName}
                        guessState={guessState}
                        onNameChange={handleNameChange}
                        isMobile={isMobile}
                    />

                    {guessState.gameCompleted ? (
                        <GameCompletion
                            firstName={targetFirstName}
                            middleName={targetMiddleName}
                            lastName={targetLastName}
                            score={guessState.score}
                            isMobile={isMobile}
                        />
                    ) : (
                        <>
                            <GuessInput
                                guessError={guessError}
                                isSubmitting={isSubmitting}
                                gameCompleted={guessState.gameCompleted}
                                score={guessState.score}
                                onSubmit={handleSubmit}
                                showHintButton={true}
                                hintsUnlocked={showInlineTriviaButton}
                                hasUnrevealedHints={inlineTrivia.some(t => !t.asked)}
                                revealedHintsCount={inlineTrivia.filter(t => t.asked).length}
                                totalHintsCount={inlineTrivia.length}
                                onRevealHint={handleInlineHintReveal}
                                isRevealingHint={isRevealingHint}
                            />

                            <InlineHintReveal
                                trivia={inlineTrivia}
                                showTrivia={showInlineTriviaButton}
                                gameCompleted={guessState.gameCompleted}
                                onRevealHint={handleInlineHintReveal}
                                game={game}
                            />
                        </>
                    )}

                    <VictoryPopup
                        open={guessState.gameWon}
                        onClose={() => setGuessState(prevState => ({ ...prevState, gameWon: false }))}
                        firstName={targetFirstName}
                        middleName={targetMiddleName}
                        lastName={targetLastName}
                        score={guessState.score}
                        isMobile={isMobile}
                    />

                    <NameQuestions
                        questions={gameQuestions}
                        recordQuestion={recordQuestion}
                        game={game}
                        gameCompleted={guessState.gameCompleted}
                    />
                </Container>
            )}
            
            <GameInstructions 
                open={showInstructions}
                onClose={() => setShowInstructions(false)}
            />
        </>
  );
};

export default NameGuess;